"use client"

import { motion } from "framer-motion"
import { useInView } from "react-intersection-observer"
import { MessageSquarePlus, Star } from "lucide-react"

export function Testimonials() {
    const [ref, inView] = useInView({
        triggerOnce: true,
        threshold: 0.1,
    })

    return (
        <section id="testimonials" aria-labelledby="testimonials-heading" className="py-32 relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-b from-background via-primary/5 to-background" />

            <div className="container mx-auto px-4 relative z-10">
                <motion.div ref={ref} className="max-w-4xl mx-auto">
                    {/* Section header */}
                    <div className="text-center mb-16 space-y-4">
                        <motion.span
                            initial={{ opacity: 0, y: 20 }}
                            animate={inView ? { opacity: 1, y: 0 } : {}}
                            transition={{ delay: 0.2 }}
                            className="inline-block text-sm font-medium px-4 py-1.5 rounded-full bg-muted/10 border border-border text-foreground backdrop-blur-sm"
                        >
                            Testimonials
                        </motion.span>
                        <motion.h2
                            id="testimonials-heading"
                            initial={{ opacity: 0, y: 20 }}
                            animate={inView ? { opacity: 1, y: 0 } : {}}
                            transition={{ delay: 0.3 }}
                            className="text-4xl md:text-6xl font-bold tracking-tight"
                        >
                            What People Say
                        </motion.h2>
                    </div>

                    {/* Placeholder Card */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={inView ? { opacity: 1, y: 0 } : {}}
                        transition={{ delay: 0.4 }}
                        className="relative"
                    >
                        <div className="p-12 md:p-16 rounded-3xl glass-panel border-dashed border-2 border-primary/30 text-center">
                            {/* Decorative stars */}
                            <div className="flex justify-center gap-1 mb-6">
                                {[...Array(5)].map((_, i) => (
                                    <motion.div
                                        key={i}
                                        initial={{ opacity: 0, scale: 0 }}
                                        animate={inView ? { opacity: 1, scale: 1 } : {}}
                                        transition={{ delay: 0.5 + i * 0.1 }}
                                    >
                                        <Star className="w-6 h-6 text-primary/30 fill-primary/20" />
                                    </motion.div>
                                ))}
                            </div>

                            <motion.div
                                initial={{ scale: 0.8, opacity: 0 }}
                                animate={inView ? { scale: 1, opacity: 1 } : {}}
                                transition={{ delay: 0.6, type: "spring" }}
                                className="w-20 h-20 rounded-full bg-gradient-to-br from-primary/20 to-secondary/20 border-2 border-dashed border-primary/30 flex items-center justify-center mx-auto mb-6"
                            >
                                <MessageSquarePlus className="w-8 h-8 text-primary/50" />
                            </motion.div>

                            <motion.h3
                                initial={{ opacity: 0 }}
                                animate={inView ? { opacity: 1 } : {}}
                                transition={{ delay: 0.7 }}
                                className="text-2xl md:text-3xl font-bold mb-3 bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent"
                            >
                                Your Review Could Be Here
                            </motion.h3>

                            <motion.p
                                initial={{ opacity: 0 }}
                                animate={inView ? { opacity: 1 } : {}}
                                transition={{ delay: 0.8 }}
                                className="text-muted-foreground max-w-md mx-auto mb-8"
                            >
                                Worked together on a project? I'd love to hear about your experience and feature your testimonial here.
                            </motion.p>

                            <motion.a
                                href="#contact"
                                initial={{ opacity: 0, y: 10 }}
                                animate={inView ? { opacity: 1, y: 0 } : {}}
                                transition={{ delay: 0.9 }}
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition-colors"
                            >
                                <MessageSquarePlus className="w-4 h-4" />
                                Leave a Review
                            </motion.a>
                        </div>

                        {/* Background decoration */}
                        <div className="absolute -inset-4 bg-gradient-to-r from-primary/5 via-transparent to-secondary/5 rounded-3xl -z-10 blur-2xl" />
                    </motion.div>
                </motion.div>
            </div>
        </section>
    )
}
